import {
  Button,
  createStyles,
  Grid,
  makeStyles,
  Step,
  StepButton,
  Stepper,
  Theme,
  Typography,
} from '@material-ui/core';
import { CheckCircle } from '@material-ui/icons';
import { CustomConnector } from 'components/customConnector';
import { MdSpace, SmSpace, LgSpace } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { RootState } from 'store';
import { resetInvestidor, updateInvestidor, hightlightErrors } from 'store/modules/investidores/registro';
import { FundoInvestidorRequest } from 'common/genericTypes';
import InvestidorParser from 'utils/parsers/investidorParser';
import Documentos from './documentos';
import InformacoesPrincipais from './informacoesPrincipais';
import RepresentantesLegais from './representantesLegais';
import ValidacaoCpfCnpj from './validacaoCpfCnpj';
import FundoInvestidor from './fundoInvestidor';
import { useValidarCampos, EtapaCadastro } from './useValidarCampos';
import { labelsPf, labelsPj } from 'pages/investidores/registro/informacoesPrincipais/labels';
import { addAlert } from 'store/modules/snackBar';
import { TipoInvestidor } from 'common/genericTypes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    iconWrapper: {
      width: '112px',
      height: '112px',
      borderRadius: '50%',
      backgroundColor: theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    icon: {
      width: '40px',
      height: '40px',
    },
  })
);

const mapStateToProps = (state: RootState) => ({
  investidor: state.investidor,
  investidorApi: state.investidorApi,
});

const mapDispatchToProps = {
  resetInvestidor,
  updateInvestidor,
  hightlightErrors,
};

type ComponentParams = { investidorId?: string };

type CadastrarInvestidorProps = RouteComponentProps<ComponentParams> &
  ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const CadastrarInvestidor: React.FC<CadastrarInvestidorProps> = (props) => {
  const {
    investidor,
    updateInvestidor,
    resetInvestidor,
    hightlightErrors,
    investidorApi: { getInvestidor, postInvestidor, putInvestidor, putUploadDoc, deleteUploadDoc, postFundoInvestidor },
  } = props;
  const [step, setStep] = useState<number>(0);
  const [finished, setFinished] = useState<boolean>(false);
  const [showFundoInvestidor, setShowFundoInvestidor] = useState<boolean>(false);
  const [fundoCnpj, setFundoCnpj] = useState<string>('');

  const steps = ['1. Informações Principais', '2. Adicionar Representantes Legais', '3. Documentos'];
  const labels = investidor.informacoesPrincipais.isPj ? labelsPj : labelsPf;

  const classes = useStyles();
  const dispatch = useDispatch();

  const { investidorId } = props.match.params;

  const handleNextStep = () => {
    if (step === steps.length) return;
    else setStep(step + 1);
  };

  const handlePreviousStep = () => {
    if (step === 1) return;
    else setStep(step - 1);
  };

  const camposInvalidosIP = useValidarCampos(labels, EtapaCadastro.InformacoesPrincipais);
  const camposInvalidosRL = useValidarCampos(labels, EtapaCadastro.RepresentantesLegais);
  const camposInvalidosDocs = useValidarCampos(labels, EtapaCadastro.Documentos);

  const isStepCompleted = (index: number) => {
    switch (index) {
      case 1:
        return camposInvalidosIP.length === 0;
      case 2:
        return camposInvalidosRL.length === 0;
      case 3:
        return camposInvalidosDocs.length === 0;
      default:
        return false;
    }
  };

  const handleReset = () => {
    setStep(0);
    setFinished(false);
    resetInvestidor();
  };

  const handleEditSubmit = async () => {
    if (!investidorId) return;

    for (const document of investidor.documentos) {
      if (document.file) await putUploadDoc(dispatch, investidorId, document.type, document.file, true);
    }

    const participanteRequest = InvestidorParser.stateToCadastroInvestidorRequest(investidor);

    await putInvestidor(dispatch, investidorId, participanteRequest, true);

    for (const documentoExcluir of investidor.documentosExcluir) {
      documentoExcluir.remoteFileInfo?.id &&
        (await deleteUploadDoc(dispatch, investidorId, documentoExcluir.remoteFileInfo.id, true));
    }
  };

  const submitInvestidor = async () => {
    const response = await postInvestidor(
      dispatch,
      InvestidorParser.stateToCadastroInvestidorRequest(investidor),
      true
    );

    if (!response || !response.id) {
      throw new Error('Não foi possível obter o ID do investidor cadastrado');
    }

    await submitInvestidorDocuments(response.id);
  };

  const submitInvestidorDocuments = async (investidorId: string) => {
    if (investidorId) {
      for (const document of investidor.documentos) {
        if (document.file) {
          await putUploadDoc(dispatch, investidorId, document.type, document.file, true);
        }
      }
    }
  };

  const camposInvalidos = useValidarCampos(labels);

  const handleSubmit = async () => {
    try {
      if (camposInvalidos.length > 0) {
        hightlightErrors(true);
        dispatch(
          addAlert({
            message: `Os seguintes campos não foram preenchidos corretamente: ${camposInvalidos.join(', ')}.`,
            severity: 'error',
          })
        );
        return;
      }
      
      setFinished(false);
      
      if (investidorId) {
        await handleEditSubmit();
      } else {
        await submitInvestidor();
      }
      
      setFinished(true);
    } catch (error) {
      setFinished(false);
    }
  };

  const handleFundoInvestidorSubmit = async (formData: FundoInvestidorRequest) => {
    try {
      setFinished(false);
      
      if (!investidorId) {
        await postFundoInvestidor(dispatch, formData, true);
      }
      
      setFinished(true);
    } catch (error) {
      console.error('Error in handleFundoInvestidorSubmit:', error);
      setFinished(false);
    }
  };
  
  const handleFundoInvestidorCancel = () => {
    setShowFundoInvestidor(false);
    setFundoCnpj('');
    setStep(0);
  };
  
  const handleFundoInvestidorStart = (cnpj: string) => {
    setFundoCnpj(cnpj);
    setShowFundoInvestidor(true);
  };

  useEffect(handleReset, [resetInvestidor]);

  useEffect(() => {
    if (investidorId) {
      getInvestidor(dispatch, investidorId, true)
        .then((response) => {
          if (response) {
            updateInvestidor(InvestidorParser.apiResponseToInvestidorState(response));
            
            if (response.tipo === TipoInvestidor.Fundo) {
              setShowFundoInvestidor(true);
            } else {
              setStep(1);
            }
          }
        })
        .catch(() => {});
    }
  }, [investidorId, dispatch, getInvestidor, updateInvestidor]);

  return (
    <div>
      <Typography variant="h1">
        {investidorId 
          ? (investidor?.informacoesPrincipais?.tipo === TipoInvestidor.Fundo
              ? 'Editar Fundo Investidor'
              : 'Editar Investidor')
          : (showFundoInvestidor 
              ? 'Cadastro de Fundo Investidor' 
              : 'Cadastro de Investidor')}
      </Typography>
      <MdSpace />
      {step !== 0 && !showFundoInvestidor && (
        <Stepper
          nonLinear
          activeStep={step - 1}
          connector={<CustomConnector />}
          style={{ padding: 0, display: 'flex', flexWrap: 'wrap' }}
        >
          {steps.map((label, index) => {
            return (
              <Step key={label} onClick={() => setStep(index + 1)}>
                <StepButton completed={isStepCompleted(index + 1)}>{label}</StepButton>
              </Step>
            );
          })}
        </Stepper>
      )}
      <MdSpace />
      <div>
        {finished ? (
          <Grid container direction="column" justify="center" alignItems="center">
            <div className={classes.iconWrapper}>
              <CheckCircle className={classes.icon} color="secondary" />
            </div>
            <SmSpace />
            <Typography variant="h3">
              {showFundoInvestidor 
                ? 'Cadastro de Fundo Investidor concluído com sucesso!' 
                : 'Cadastro de Investidor concluído com sucesso!'}
            </Typography>
            <MdSpace />
            <Button
              onClick={!investidorId ? handleReset : () => props.history.goBack()}
              variant="contained"
              color="primary"
            >
              {'Voltar para a página inicial'}
            </Button>
            <MdSpace />
          </Grid>
        ) : showFundoInvestidor ? (
          <FundoInvestidor 
            cnpj={fundoCnpj} 
            onSubmit={handleFundoInvestidorSubmit} 
            onCancel={handleFundoInvestidorCancel}
            investidorId={investidorId}
          />
        ) : (
          <>
            {step === 0 && (
              <ValidacaoCpfCnpj 
                onSubmit={handleNextStep} 
                onSubmitFundoInvestidor={handleFundoInvestidorStart} 
              />
            )}
            {step === 1 && <InformacoesPrincipais />}
            {step === 2 && <RepresentantesLegais />}
            {step === 3 && <Documentos />}
            <MdSpace />
            {!!step && (
              <Grid container justify="center" spacing={8} alignItems="center" style={{ width: '100%' }}>
                <Grid item>
                  <Button variant="outlined" disabled={step === 1} onClick={handlePreviousStep} data-testId="btnPassoAnteriorInvestidor">
                    {'Passo Anterior'}
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="outlined" disabled={step === steps.length} onClick={handleNextStep} data-testId="btnProximoPassoInvestidor">
                    {'Próximo Passo'}
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="contained" color="primary" onClick={handleSubmit} data-testId="btnConcluirCadastroInvestidor">
                    {'Concluir'}
                  </Button>
                </Grid>
              </Grid>
            )}
            <LgSpace />
          </>
        )}
      </div>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CadastrarInvestidor);
